import React, { useEffect, useState } from "react";

import { GetServerSidePropsContext } from "next";
import { AppProps } from "next/app";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { Queries } from "@api/graphql/queries/Queries";
import {
    AnyProduct,
    listPublications,
    listPublicationsVariables,
    offerProducts,
    offerProductsVariables,
    offerProducts_offer,
    Publication,
} from "@api/graphql/types";
import { NormalProduct, productIsBundle, productIsDigital, productIsPrint } from "@api/graphql/typeHelpers";
import { createSSRClient, ssrRequest } from "@api/GraphQLClient";
import { AccordionCard } from "@components/Card/AccordionCard";
import CartProductAddedModal from "@components/Cart/CartProductAddedModal";
import { Flex } from "@components/Flex";
import { Meta } from "@components/Meta";
import { RecommendedProducts } from "@components/RecommendedProducts";
import { Typography } from "@components/Typography";
import { ApplicationState } from "@redux/reducers";
import { bp } from "@styles/theme";
import { ImageUtils } from "@utils/ImageUtils";
import { useManualQuery } from "graphql-hooks";
import { useLoctool } from "@bigfish/react-loctool";
import { Cookie } from "@utils/Cookie";
import { ProductOffers } from "@components/ProductOffers";
import { AlertStrip } from "@components/AlertStrip";
import { HtmlUtils } from "@utils/HtmlUtils";

interface ComponentProps {
    publication: Publication;
}

type Props = ComponentProps & AppProps;

const ProductPage = (props: Props) => {
    const Intl = useLoctool();
    const { publication } = props;
    const [productAddedToCart, setProductAddedToCart] = useState<AnyProduct | null>(null);
    const [recommendedProducts, setRecommendedProducts] = useState<AnyProduct[]>([]);
    const cart = useSelector((state: ApplicationState) => state.cart);
    const userData = useSelector((state: ApplicationState) => state.auth.user);
    const cartItemWithSubscription = cart.normalCart.items.find(
        item => !productIsBundle(item.product) && item.product.publication.id === publication.id && item.product.current_subscription_end !== null,
    );

    useEffect(() => {
        setRecommendedProducts(cart.recommendedProducts);
    }, []);

    const [offersQuery] = useManualQuery<offerProducts, offerProductsVariables>(Queries.offerProducts);
    const [offers, setOffers] = useState<offerProducts_offer[] | undefined>(undefined);

    useEffect(() => {
        const loadOffers = async (code: string) => {
            const offerData = await offersQuery({ variables: { code } });
            if (offerData.data) {
                setOffers([...(offers ?? []), offerData.data.offer]);
            }
        };
        if (window && window.sessionStorage.getItem("offerId") && window.sessionStorage.getItem("offerPubliactionIds")) {
            const offerPublicationIds = JSON.parse(window.sessionStorage.getItem("offerPubliactionIds") ?? "");
            if (offerPublicationIds && (offerPublicationIds as number[]).includes(publication.id)) {
                loadOffers(window.sessionStorage.getItem("offerId") ?? "");
            }
        }
        if (userData && userData.offer_ids) {
            Promise.all(userData.offer_ids.map(id => loadOffers(id)));
        }
    }, []);

    const handleModal = (product: AnyProduct) => {
        setProductAddedToCart(product);
    };

    const closeModal = () => {
        setProductAddedToCart(null);
    };

    return (
        <>
            <Meta
                title={`${publication.name} - ${Intl.formatMessage({ id: "common.brandName" })}`}
                description={publication.lead ?? Intl.formatMessage({ id: "common.brandDescription" })}
                ogTitle={`${publication.name} - ${Intl.formatMessage({ id: "common.brandName" })}`}
                ogDescription={publication.lead ?? Intl.formatMessage({ id: "common.brandDescription" })}
                ogImage={ImageUtils.publicationImageWithFallback(publication, { width: 270 }).src}
            />
            {cartItemWithSubscription && (
                <AlertStrip
                    type="warning"
                    label={Intl.formatMessage(
                        { id: "pages.product.currentSubscriptionEnd" },
                        { date: Intl.formatDate(cartItemWithSubscription.product.current_subscription_end ?? "") },
                    )}
                />
            )}
            <ProductContainer className="_ce_measure_row">
                <ProductHeader className="product-header _ce_measure_column">
                    <ProductTitle>{publication.name}</ProductTitle>
                    <ProductImage {...ImageUtils.publicationImageWithFallback(publication, { width: 270 })} alt={publication.name} />
                    <ProductDescription>
                        {publication?.lead ? <div dangerouslySetInnerHTML={{ __html: HtmlUtils.sanitizeWithLinkSecurity(publication?.lead) }} /> : null}
                    </ProductDescription>
                </ProductHeader>
                <ProductBody className="_ce_measure_column">
                    <ProductOffers
                        products={[...publication.products, ...publication.digital_products, ...(publication.bundles ?? [])]}
                        handleModal={handleModal}
                        offers={offers}
                        publicationId={publication.id}
                        publicationName={publication.name}
                        publicationShortName={publication.short_name}
                        publicationImage={ImageUtils.publicationImageWithFallback(publication, { width: 540 })}
                    />
                </ProductBody>
            </ProductContainer>
            {recommendedProducts.length > 0 && <RecommendedProducts products={recommendedProducts} />}
            {productAddedToCart && <CartProductAddedModal mounted={true} product={productAddedToCart as NormalProduct} onClose={() => closeModal()} />}
        </>
    );
};

export async function getServerSideProps(context: GetServerSidePropsContext<{ id: string; digital?: string }>) {
    const storedAuthToken = Cookie.getAuthToken(context.req);
    const client = createSSRClient();
    client.setHeader("Authorization", `Bearer ${storedAuthToken}`);

    const publicationId = parseInt(context.query.id as string);
    const digital = !!context.query.digital;
    const referer = context.req.headers.referer;
    if (referer && digital) {
        const host = new URL(referer).host;
        if (
            ["24.hu", "nl.hu", "sport24.24.hu", "nosalty.hu", "staging.noklapja.hu", "staging.24.p24.hu", "sport24.staging.24.p24.hu", "noklapja.hu"].includes(
                host,
            )
        ) {
            Cookie.setRedirectUrl(referer as string, context.res);
        }
    }
    const { data } = await ssrRequest<listPublications, listPublicationsVariables>(
        {
            query: Queries.listPublications,
            variables: { first: 1, filters: { id: publicationId } },
        },
        { client, useCache: false },
    );

    const publication = data?.listPublications?.data[0];

    if (publication) {
        if (publication.default_product) {
            if (productIsDigital(publication.default_product) && publication.digital_products.length === 0) {
                publication.digital_products.push(publication.default_product);
            }
            if (productIsPrint(publication.default_product) && publication.products.length === 0) {
                publication.products.push(publication.default_product);
            }
        }
        return {
            props: {
                publication,
            },
        };
    }

    return {
        notFound: true,
    };
}

export default ProductPage;

const ProductHeader = styled(Flex.Container).attrs({
    $flexDirection: "column",
})`
    width: 100%;
    max-width: 360px;
    padding: 24px 16px;
    margin: 0 auto;

    ${bp.large} {
        // Position sticky is not compatible with the solution of react-aria-modal for disable scrolling, causes jumping on sticky elements
        // Task: Find solution
        // position: sticky;
        // top: 20px;
        align-self: start;
        max-width: 420px;
        padding: 0;
    }
`;

const ProductTitle = styled(Typography.H1)`
    margin-bottom: 24px;
    text-align: center;

    ${bp.large} {
        text-align: left;
    }
`;

const ProductImage = styled.img`
    width: 100%;
    max-width: 180px;
    margin: 0 auto 50px auto;
    object-fit: contain;

    ${bp.large} {
        width: 80%;
        max-width: 270px;
        margin: 0 0 50px;
        object-position: left;
    }
`;

const ProductDescription = styled(Typography.Medium)``;

const StyledAccordionCard = styled(AccordionCard)`
    box-shadow: none;
    border: 1px solid transparent;
    border-top-color: ${props => props.theme.color.line};
    border-radius: 0;
    z-index: 1;
    transition: border-radius 300ms, border-color 300ms, box-shadow 300ms, z-index 0s 0s;

    &:hover {
        border-color: ${props => props.theme.color.line};
        border-bottom-color: transparent;
        border-radius: ${props => props.theme.radius.medium};
        box-shadow: ${props => props.theme.shadow.card};
        z-index: 2;
    }
`;

const ProductBody = styled.div`
    ${StyledAccordionCard}:last-child {
        border-bottom: 1px solid ${props => props.theme.color.line};
    }
`;

const ProductContainer = styled.div`
    max-width: 1440px;
    margin: 0 auto;

    ${bp.large} {
        width: 100%;
        display: grid;
        align-self: start;
        grid-template-columns: 0.5fr 1fr;
        grid-column-gap: 40px;
        padding: 40px 50px;
    }
`;
